import React, {useEffect, useMemo, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import ForecastDropdown from "./ForecastDropdown";
import CustomerDropdown from "./CustomerDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import SkuBaseForecastTable from "./SkuBaseForecastTable";
import UnitCostForecastTable from "./UnitCostForecastTable";
import DistributionForecastTable from "./DistributionForecastTable";
import RateOfSaleForecastTable from "./RateOfSaleForecastTable";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import PromotionsTable from "./PromotionsTable";
import {
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Snackbar, Skeleton,
    CircularProgress
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {Addchart} from "@mui/icons-material";
import axiosInstance from "../AxiosInstance";
import './Styles.css';
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`forecast-tabpanel-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 2 }}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const CustomerForecast = () => {
    const [forecasts, setForecasts] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [selectedForecastId, setSelectedForecastId] = useState(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [promotionProductsData, setPromotionProductsData] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentForecastPeriodName, setCurrentForecastPeriodName] = useState("");
    const [hasForecastWeekElapsed, setHasForecastWeekElapsed] = useState(false);
    const [newForecastPeriodName, setNewForecastPeriodName] = useState("");
    const [validationError, setValidationError] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");
    const [loadingForecasts, setLoadingForecasts] = useState(true);
    const [skuBaseForecasts, setSkuBaseForecasts] = useState([]);
    const [isCreateForecastDialogOpen, setIsCreateForecastDialogOpen] = useState(false);
    const [copyCurrentForecastData, setCopyCurrentForecastData] = useState(false);
    const [isCreatingForecast, setIsCreatingForecast] = useState(false);
    const [isCreatingForecastPeriod, setIsCreatingForecastPeriod] = useState(false);
    const isCreatingForecastRef = useRef(false);
    const isCreatingForecastPeriodRef = useRef(false);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const [hasUserSelectedAll, setHasUserSelectedAll] = useState(false);
    const location = useLocation();
    const isLocked = location.state?.isLocked || false;
    const [tabIndex, setTabIndex] = useState(0);

    const handleTabChange = (_, newValue) => setTabIndex(newValue);

    const getToken = () => localStorage.getItem("token");

    useEffect(() => {
        // Restore localStorage loading for persistence across page visits
        const storedForecast = JSON.parse(localStorage.getItem("selectedForecast"));
        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId) {
            setSelectedForecastPeriod(storedForecastPeriodId);
        }

        fetchForecastsAndPeriods();
    }, []);

    useEffect(() => {
        if (!loadingForecasts) {
            setIsInitialLoadComplete(true);
        }
    }, [loadingForecasts]);

    useEffect(() => {
        if (selectedForecastId && selectedForecastPeriodId) {
            const forecast = forecasts.find((f) => f.forecastId === selectedForecastId);
            const forecastPeriod = forecast?.forecastPeriods.find(
                (fp) => fp.forecastPeriodId === Number(selectedForecastPeriodId)
            );
            if (forecastPeriod) {
                setCurrentForecastPeriodName(forecastPeriod.name);
            } else {
                setCurrentForecastPeriodName("");
            }
        } else {
            setCurrentForecastPeriodName("");
        }
    }, [selectedForecastId, selectedForecastPeriodId, forecasts]);

    useEffect(() => {
        if (selectedForecastId && selectedForecastPeriodId && selectedCustomerId) {
            fetchForecastsData();
            getPromotions();
        }
    }, [selectedForecastId, selectedForecastPeriodId, selectedCustomerId]);

    // Check if all required selections are made
    useEffect(() => {
        const allSelected = selectedForecastId && selectedForecastPeriodId && selectedCustomerId && isInitialLoadComplete;
        setHasUserSelectedAll(!!allSelected);
    }, [selectedForecastId, selectedForecastPeriodId, selectedCustomerId, isInitialLoadComplete]);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get("/forecasts", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setForecasts(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const fetchForecastsData = async () => {
        try {
            const response = await axiosInstance.get(`/forecasts/${selectedForecastId}/skubaseforecasts/${selectedCustomerId}?forecastPeriodId=${selectedForecastPeriodId}`);
            const viewModels = response.data;
            setSkuBaseForecasts(viewModels);
        } catch (error) {
            console.error('Error fetching forecast data:', error);
            const errorMessage = error.response?.data || 'Failed to load forecast data. Please try again.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleForecastSelected = (id) => {
        localStorage.setItem("selectedForecast", JSON.stringify({forecastId: id}));
        setSelectedForecastId(id);
        setSelectedForecastPeriodId(null);
        setCurrentForecastPeriodName("");
        setHasForecastWeekElapsed(false);
    };

    const setSelectedForecastPeriod = (forecastPeriodId) => {
        if (forecastPeriodId) {
            localStorage.setItem("selectedForecastPeriodId", forecastPeriodId);
            setSelectedForecastPeriodId(forecastPeriodId);

            const forecast = forecasts.find((f) => f.forecastId === selectedForecastId);
            const forecastPeriod = forecast?.forecastPeriods.find(
                (fp) => fp.forecastPeriodId === Number(forecastPeriodId)
            );

            if (forecastPeriod) {
                setCurrentForecastPeriodName(forecastPeriod.name);
                setHasForecastWeekElapsed(forecastPeriod.hasForecastWeekElapsed);
            }
        } else {
            localStorage.removeItem("selectedForecastPeriodId");
            setSelectedForecastPeriodId(null);
            setCurrentForecastPeriodName("");
            setHasForecastWeekElapsed(false);
        }
    };

    const handleForecastPeriodSelected = (forecastPeriodId) => {
        setSelectedForecastPeriod(forecastPeriodId);
        setPromotionProductsData(null); // Clear highlighted cells when forecast period changes
    };

    const handleCustomerSelected = (selection) => {
        // Clear existing forecast data immediately to avoid showing stale data while loading
        setSkuBaseForecasts([]);
        setPromotions([]);
        setPromotionProductsData(null);

        if (typeof selection === "object" && selection !== null) {
            if (selection.type === "customer") {
                setSelectedCustomerId(selection.id);
            } else {
                setSelectedCustomerId(null);
            }
        } else {
            setSelectedCustomerId(selection);
        }
    };

    const handlePromotionProductsDataChange = (data) => {
        setPromotionProductsData(data);
    };

    const handleCreateForecastClick = () => setIsCreateForecastDialogOpen(true);
    const handleCloseCreateForecastDialog = () => {
        if (!isCreatingForecast) {
            setIsCreateForecastDialogOpen(false);
            setCopyCurrentForecastData(false);
        }
    };

    const handleCreateForecastSubmit = async () => {
        if (isCreatingForecastRef.current) {
            return;
        }

        isCreatingForecastRef.current = true;
        setIsCreatingForecast(true);
        try {
            const token = getToken();
            const payload = {
                isCopyCurrentForecast: copyCurrentForecastData
            };
            await axiosInstance.post("/forecasts", payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Fetch updated forecasts
            const updated = await axiosInstance.get("/forecasts", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setForecasts(updated.data);

            // Use last item assuming API returns forecasts sorted by date ascending
            const newestForecast = updated.data[updated.data.length - 1];
            if (newestForecast) {
                setSelectedForecastId(newestForecast.forecastId);
                localStorage.setItem("selectedForecast", JSON.stringify({ forecastId: newestForecast.forecastId }));

                const firstPeriod = newestForecast.forecastPeriods?.[0];
                if (firstPeriod) {
                    setSelectedForecastPeriodId(firstPeriod.forecastPeriodId);
                    localStorage.setItem("selectedForecastPeriodId", firstPeriod.forecastPeriodId);
                    setCurrentForecastPeriodName(firstPeriod.name);
                    setHasForecastWeekElapsed(firstPeriod.hasForecastWeekElapsed || false);
                }
            }

            setSnackbarMessage("Forecast created successfully!");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage(error.response?.data || "Failed to create forecast.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsCreatingForecast(false);
            isCreatingForecastRef.current = false;
            handleCloseCreateForecastDialog();
        }
    };


    const handleDialogClose = () => {
        if (isCreatingForecastPeriod) {
            return;
        }

        setIsDialogOpen(false);
        setValidationError("");
        setNewForecastPeriodName("");
    };

    const handleCreateForecastPeriod = async () => {
        if (isCreatingForecastPeriodRef.current) {
            return;
        }

        const nameRegex = /^[a-zA-Z0-9\s]+$/;
        if (!nameRegex.test(newForecastPeriodName.trim())) {
            setValidationError("Please enter a valid name. Only alphanumeric characters and spaces are allowed.");
            return;
        }

        isCreatingForecastPeriodRef.current = true;
        setIsCreatingForecastPeriod(true);
        setValidationError("");
        try {
            await axiosInstance.post(`/forecasts/${selectedForecastId}/forecastperiods`, {
                name: newForecastPeriodName.trim(),
            });

            const token = getToken();
            const response = await axiosInstance.get("/forecasts", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setForecasts(response.data);

            const updatedForecast = response.data.find((f) => f.forecastId === selectedForecastId);
            if (updatedForecast) {
                const newForecastPeriod = updatedForecast.forecastPeriods.find(
                    (fp) => fp.name === newForecastPeriodName.trim()
                );
                if (newForecastPeriod) {
                    setSelectedForecastPeriodId(newForecastPeriod.forecastPeriodId);
                    setCurrentForecastPeriodName(newForecastPeriod.name);
                    setHasForecastWeekElapsed(false);
                }
            }

            setSnackbarMessage("Forecast period created successfully!");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            setIsDialogOpen(false);
        } catch (error) {
            console.error("Failed to create forecast period:", error);

            setSnackbarMessage(error.response?.data || "An unexpected error occurred.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsCreatingForecastPeriod(false);
            isCreatingForecastPeriodRef.current = false;
            setNewForecastPeriodName("");
        }
    };

    const openCreateForecastPeriodDialog = () => {
        const sortedForecastPeriods = selectedForecast
            ? [...selectedForecast.forecastPeriods].sort((a, b) => a.periodIndex - b.periodIndex)
            : [];
        const firstUnlockedPeriod = sortedForecastPeriods.find((fp) => !fp.isLocked);

        if (firstUnlockedPeriod) {
            setCurrentForecastPeriodName(firstUnlockedPeriod.name);
        }

        setIsDialogOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
    };

    const selectedForecast = useMemo(() => {
        return forecasts.find((f) => f.forecastId === selectedForecastId);
    }, [forecasts, selectedForecastId]);

    const selectedForecastPeriod = useMemo(() => {
        return selectedForecast?.forecastPeriods?.find((fp) => fp.forecastPeriodId === selectedForecastPeriodId);
    }, [selectedForecast, selectedForecastPeriodId]);

    const isForecastPeriodLocked = selectedForecastPeriod?.isLocked || false;

    const getPromotions = async () => {
        try {
            const response = await axiosInstance.get(
                `/forecasts/${selectedForecastId}/promotions/${selectedCustomerId}?forecastPeriodId=${selectedForecastPeriodId}`
            );
            setPromotions(response.data || []);
        } catch (error) {
            console.error("Error fetching promotions:", error);
        }
    };

    return (
        <Box className="component-container">
            <Box sx={{display: "flex", alignItems: "center", gap: 2, pb: 2}}>
                {loadingForecasts ? (
                    <Skeleton variant="rectangular" width={200} height={40}/>
                ) : (
                    <ForecastDropdown
                        forecasts={forecasts}
                        selectedForecastId={selectedForecastId}
                        onForecastSelected={handleForecastSelected}
                    />
                )}
                {loadingForecasts ? (
                    <Skeleton variant="circular" width={32} height={32}/>
                ) : (
                    <Tooltip title="Create new forecast year">
                        <IconButton
                            aria-label="createForecast"
                            onClick={handleCreateForecastClick}
                            disabled={loadingForecasts}
                        >
                            <Addchart />
                        </IconButton>
                    </Tooltip>
                )}
                {loadingForecasts ? (
                    <Skeleton variant="rectangular" width={200} height={40}/>
                ) : (
                    <ForecastPeriodDropdown
                        forecastPeriods={selectedForecast ? selectedForecast.forecastPeriods : []}
                        selectedForecastPeriodId={selectedForecastPeriodId}
                        onForecastPeriodSelected={handleForecastPeriodSelected}
                    />
                )}
                {loadingForecasts ? (
                    <Skeleton variant="circular" width={32} height={32}/>
                ) : (
                    <Tooltip title="Create new forecast period">
                        <IconButton
                            aria-label="createForecastPeriod"
                            onClick={openCreateForecastPeriodDialog}
                            disabled={!selectedForecastPeriodId}
                        >
                            <Addchart/>
                        </IconButton>
                    </Tooltip>
                )}
                {loadingForecasts ? (
                    <Skeleton variant="rectangular" width={200} height={40}/>
                ) : (
                    <CustomerDropdown onCustomerSelected={handleCustomerSelected}/>
                )}
            </Box>
            <Dialog open={isCreateForecastDialogOpen} onClose={handleCloseCreateForecastDialog} disableEscapeKeyDown={isCreatingForecast}>
                <DialogTitle>Create Forecast</DialogTitle>
                <DialogContent>
                    {isCreatingForecast ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
                            <CircularProgress />
                            <Typography>Creating forecast{copyCurrentForecastData ? ' and copying data' : ''}...</Typography>
                        </Box>
                    ) : (
                        <>
                            <p>
                                Are you sure you wish to create the next forecast year?
                            </p>
                            <Box mt={2}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={copyCurrentForecastData}
                                        onChange={(e) => setCopyCurrentForecastData(e.target.checked)}
                                        disabled={isCreatingForecast}
                                    />
                                    {" Copy current customer base forecast data from current forecast year"}
                                </label>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreateForecastDialog} color="secondary" disabled={isCreatingForecast}>Cancel</Button>
                    <Button onClick={handleCreateForecastSubmit} color="primary" disabled={isCreatingForecast}>
                        {isCreatingForecast ? 'Creating...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isDialogOpen} onClose={handleDialogClose} disableEscapeKeyDown={isCreatingForecastPeriod}>
                <DialogTitle>Create Forecast Period</DialogTitle>
                <DialogContent>
                    <p>
                        Are you sure you wish to create the next forecast period? The current forecast period{" "}
                        <strong>{currentForecastPeriodName}</strong> will be locked. This action cannot be undone.
                    </p>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Forecast Period Name"
                        fullWidth
                        value={newForecastPeriodName}
                        onChange={(e) => setNewForecastPeriodName(e.target.value)}
                        error={!!validationError}
                        helperText={validationError}
                        disabled={isCreatingForecastPeriod}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary" disabled={isCreatingForecastPeriod}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreateForecastPeriod} color="primary" disabled={isCreatingForecastPeriod}>
                        {isCreatingForecastPeriod ? "Creating..." : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
            {hasUserSelectedAll && (
                <Box>
                    <Box sx={{display: "flex-end", gap: 2, pb: 2}}>
                        <Tabs value={tabIndex} onChange={handleTabChange}>
                            <Tab label="Base" />
                            <Tab label="Unit Cost" />
                            <Tab label="Distribution" />
                            <Tab label="Rate of Sale" />
                        </Tabs>

                        <TabPanel value={tabIndex} index={0}>
                            <SkuBaseForecastTable
                                key={`sbf-${selectedForecastId || 'na'}-${selectedForecastPeriodId || 'na'}-${selectedCustomerId || 'na'}`}
                                forecastId={selectedForecastId}
                                forecastPeriodId={selectedForecastPeriodId}
                                customerId={selectedCustomerId}
                                isLocked={isLocked}
                                promotionProductsData={promotionProductsData}
                                hasForecastWeekElapsed={hasForecastWeekElapsed}
                                onRefreshPromotions={getPromotions}
                                rows={skuBaseForecasts}
                                onReload={fetchForecastsData}
                            />
                        </TabPanel>

                        <TabPanel value={tabIndex} index={1}>
                            <UnitCostForecastTable
                                forecastId={selectedForecastId}
                                forecastPeriodId={selectedForecastPeriodId}
                                isLocked={isLocked}
                                promotionProductsData={promotionProductsData}
                                rows={skuBaseForecasts}
                                onReload={fetchForecastsData}
                            />
                        </TabPanel>

                        <TabPanel value={tabIndex} index={2}>
                            <DistributionForecastTable
                                forecastId={selectedForecastId}
                                forecastPeriodId={selectedForecastPeriodId}
                                isLocked={isLocked}
                                promotionProductsData={promotionProductsData}
                                rows={skuBaseForecasts}
                                onReload={fetchForecastsData}
                                onRefreshPromotions={getPromotions}
                            />
                        </TabPanel>

                        <TabPanel value={tabIndex} index={3}>
                            <RateOfSaleForecastTable
                                forecastId={selectedForecastId}
                                forecastPeriodId={selectedForecastPeriodId}
                                isLocked={isLocked}
                                promotionProductsData={promotionProductsData}
                                rows={skuBaseForecasts}
                                onReload={fetchForecastsData}
                                onRefreshPromotions={getPromotions}
                            />
                        </TabPanel>
                    </Box>
                    <Box sx={{display: "flex-end", gap: 2, pb: 2}}>
                        <PromotionsTable
                            key={`promos-${selectedForecastId || 'na'}-${selectedForecastPeriodId || 'na'}-${selectedCustomerId || 'na'}`}
                            forecastId={selectedForecastId}
                            forecastPeriodId={selectedForecastPeriodId}
                            customerId={selectedCustomerId}
                            onRefreshSkuBaseForecasts={fetchForecastsData}
                            onPromotionProductsDataChange={handlePromotionProductsDataChange}
                            promotions={promotions}
                            onRefreshPromotions={getPromotions}
                            isForecastPeriodLocked={isForecastPeriodLocked}
                            skuBaseForecasts={skuBaseForecasts}
                        />
                    </Box>
                </Box>
            )}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{width: "100%"}}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default CustomerForecast;
